import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, TrendingUp, ShieldCheck, ExternalLink, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";

export default function CashoutPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [connect, setConnect] = useState(null);
  const [amount, setAmount] = useState("");
  const [source, setSource] = useState("earnings");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, s] = await Promise.all([api.get("/wallet/me"), api.get("/connect/status")]);
      setWallet(w.data.wallet);
      setConnect(s.data);
    } catch {
      toast.error("Couldn't load wallet");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load, params]);

  const availableCents = wallet ? (source === "gift" ? wallet.earnings_available_credits : wallet.earnings_available_credits) : 0;
  const amountCents = Math.max(0, Math.round(parseFloat(amount || "0") * 100));

  const previewFee = async () => {
    if (amountCents <= 0) { setPreview(null); return; }
    try {
      const { data } = await api.post("/cashout/preview", { amount_cents: amountCents, source });
      setPreview(data);
    } catch (e) {
      setPreview(null);
      toast.error(e.response?.data?.detail || "Invalid amount");
    }
  };

  useEffect(() => { previewFee(); /* eslint-disable-next-line */ }, [amountCents, source]);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stripe onboarding unavailable right now");
    } finally {
      setBusy(false);
    }
  };

  const confirmCashout = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const { data } = await api.post("/cashout/request", { amount_cents: amountCents, source });
      toast.success(`Withdrawal of $${(data.net_cents / 100).toFixed(2)} started`);
      await load();
      setAmount("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Withdrawal failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-8 text-zinc-500">Loading…</div>;

  const canPayout = connect?.has_account && connect?.payouts_enabled;

  return (
    <div className="max-w-lg mx-auto px-4 pt-5 pb-40 space-y-5" data-testid="cashout-page">
      <button
        onClick={() => navigate("/wallet")}
        className="inline-flex items-center text-sm text-zinc-400 hover:text-white"
        data-testid="cashout-back-btn"
      >
        <ChevronLeft className="w-4 h-4" /> Wallet
      </button>

      <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Withdraw earnings</h1>

      {/* Connect status */}
      <div
        className={`rounded-2xl p-4 border ${canPayout ? "border-cyan-400/30 bg-cyan-400/[0.05]" : "border-yellow-400/30 bg-yellow-400/[0.05]"}`}
        data-testid="connect-status"
      >
        <div className="flex items-center gap-2">
          {canPayout ? (
            <ShieldCheck className="w-4 h-4 text-cyan-300" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-yellow-300" />
          )}
          <p className="text-sm font-black">
            {!connect?.has_account
              ? "Set up your payout account"
              : canPayout
              ? "Payouts enabled"
              : "Complete Stripe onboarding"}
          </p>
        </div>
        <p className="text-xs text-zinc-400 mt-1">
          {!connect?.has_account
            ? "Hidden Gem sends withdrawals through Stripe. You'll finish account verification on Stripe's hosted page."
            : canPayout
            ? "Stripe is ready to receive your withdrawals."
            : "Stripe still needs some information before you can be paid."}
        </p>
        {!canPayout && (
          <Button
            onClick={startOnboarding}
            disabled={busy}
            className="mt-3 rounded-full h-9 px-4 bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-bold"
            data-testid="connect-onboard-btn"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> {connect?.has_account ? "Continue on Stripe" : "Start onboarding"}
          </Button>
        )}
      </div>

      {/* Amount form */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setSource("earnings")}
            className={`h-9 rounded-full text-xs font-black ${source === "earnings" ? "bg-cyan-400 text-black" : "bg-white/5 border border-white/10"}`}
            data-testid="cashout-source-earnings"
          >
            Order earnings
          </button>
          <button
            onClick={() => setSource("gift")}
            className={`h-9 rounded-full text-xs font-black ${source === "gift" ? "bg-cyan-400 text-black" : "bg-white/5 border border-white/10"}`}
            data-testid="cashout-source-gift"
          >
            Gift earnings
          </button>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Available balance</p>
          <p className="text-2xl font-black inline-flex items-center gap-1.5 tabular-nums" data-testid="cashout-available">
            <TrendingUp className="w-4 h-4 text-cyan-300" /> ${(availableCents / 100).toFixed(2)}
          </p>
        </div>

        <div>
          <label className="text-xs text-zinc-400 font-bold">Amount ($)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-1 w-full h-11 rounded-2xl bg-zinc-950 border border-white/10 text-white px-4 tabular-nums"
            data-testid="cashout-amount"
            placeholder="Minimum $10.00"
          />
        </div>

        {preview && (
          <div className="rounded-xl bg-black/40 border border-white/5 p-3 text-sm space-y-1" data-testid="cashout-preview">
            <div className="flex justify-between"><span className="text-zinc-400">Withdrawal</span><span className="tabular-nums">${(preview.amount_cents/100).toFixed(2)}</span></div>
            <div className="flex justify-between text-yellow-200" data-testid="cashout-fee">
              <span>Cash-out fee ({source === "gift" ? "configurable" : "2%"})</span>
              <span className="tabular-nums">−${(preview.fee_cents/100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-white/10 font-black">
              <span>You'll receive</span>
              <span className="tabular-nums text-cyan-300" data-testid="cashout-net">${(preview.net_cents/100).toFixed(2)}</span>
            </div>
          </div>
        )}

        <Button
          onClick={confirmCashout}
          disabled={busy || !preview || !canPayout || amountCents > availableCents}
          className="w-full h-11 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black font-bold disabled:opacity-40"
          data-testid="cashout-confirm-btn"
        >
          {busy ? "Processing…" : preview ? `Withdraw $${(preview.amount_cents/100).toFixed(2)}` : "Enter an amount"}
        </Button>
        {amountCents > availableCents && (
          <p className="text-xs text-red-300 text-center">Exceeds your available balance.</p>
        )}
      </div>
    </div>
  );
}
